'use strict';

/**
 * PictView-BlueprintStudio
 * ------------------------
 * The design canvas for a blueprint's LAYOUT: semantic region blocks arranged on a proportional grid
 * (source/BlueprintLayout.js). You drop regions (a diagram, a column of blurbs, an image, a note), drag
 * and resize them on the grid, edit their text in the inspector, and switch the DRAWING STYLE (clean /
 * drafting / blueprint) and the PREVIEW SIZE (web / Letter / ArchD). Because placement is grid cells and
 * type scales in container units, the one layout renders unchanged to the shared web page, a Letter PDF
 * and a 24x36 ARCH D sheet; the style is pure CSS tokens over the same structure.
 *
 * The chrome (palette, inspector, controls) is template-driven; the canvas itself is painted imperatively
 * (renderLayout + fitLayout into #BPS-Canvas) and the drag/resize are pointer handlers on the stage, the
 * same way the rail splitter and the doc editor own their own DOM. A full view render is only used to
 * rebuild the chrome; ordinary edits repaint just the canvas (so an inspector field keeps focus).
 *
 * Shown via #/blueprint/:id/design (Pict-Application._renderBlueprintStudio). The layout persists on the
 * Blueprint's Layout field (a draft also lives in localStorage so an in-progress design survives a reload
 * before a save).
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');
const libLayout = require('../BlueprintLayout.js');
const libFrameworkClient = require('../providers/FrameworkClient.js');

const _SELF = "_Pict.views['Plansheet-BlueprintStudio']";
const _DRAFT_KEY = 'plansheet.blueprint.layout.';
const _SIZES = { web: [1040, 660], letter: [8.5, 11], archd: [24, 36] };

function _esc(pText)
{
	return String(pText == null ? '' : pText).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
const _clamp = (pV, pA, pB) => Math.max(pA, Math.min(pB, pV));

class PictViewBlueprintStudio extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._id = 0;
		this._layout = null;
		this._selected = -1;
		this._size = 'letter';
		this._kit = 'drafting';
		this._printSheet = 'Letter';
		this._wired = false;
		this._drag = null;
	}

	// ─── open / load ────────────────────────────────────────────────────────
	open(pID)
	{
		this._id = parseInt(pID, 10) || 0;
		this._selected = -1;
		this._layout = this._loadDraft(this._id) || this._defaultLayout({ Name: 'Blueprint ' + this._id });
		if (this._layout.Kit) { this._kit = this._layout.Kit; } else { this._layout.Kit = this._kit; }
		this.render();
		let tmpBP = this.pict.providers.BlueprintAPI;
		if (tmpBP && typeof tmpBP.readSubject === 'function' && this._id)
		{
			tmpBP.readSubject({ Kind: 'record', EntityKey: 'Blueprint', ID: this._id }).then((pData) =>
			{
				if (!pData) { return; }
				let tmpStored = this._parse(pData.Layout);
				// A saved server layout wins only when we had no local draft in progress.
				if (tmpStored && !this._loadDraft(this._id)) { this._layout = tmpStored; }
				else
				{
					let tmpTitle = this._titleRegion();
					if (tmpTitle && tmpTitle.Name === ('Blueprint ' + this._id) && pData.Name) { tmpTitle.Name = pData.Name; }
				}
				this.render();
			}).catch(() => { /* seeded default stands */ });
		}
	}

	_titleRegion() { return (this._layout && this._layout.Regions || []).find((pR) => pR.Kind === 'title'); }
	_parse(pJSON) { try { let tmpO = JSON.parse(pJSON); return (tmpO && tmpO.Regions) ? tmpO : null; } catch (pErr) { return null; } }
	_loadDraft(pID) { try { return this._parse(window.localStorage.getItem(_DRAFT_KEY + pID)); } catch (pErr) { return null; } }

	_defaultLayout(pData)
	{
		let tmpName = (pData && pData.Name) || 'Untitled blueprint';
		return {
			Grid: { Cols: 12, Rows: 16 },
			Regions:
			[
				{ Kind: 'title', Col: 1, Row: 1, ColSpan: 12, RowSpan: 2, Mark: 'BP', Name: tmpName, Tagline: 'A one-line description of this blueprint.' },
				{ Kind: 'diagram', Col: 1, Row: 3, ColSpan: 8, RowSpan: 8, Caption: 'Diagram',
					SVG: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 70" fill="none" stroke="currentColor" stroke-width="2"><rect x="8" y="12" width="40" height="22"/><rect x="72" y="38" width="40" height="22"/><path d="M48 23 H72 V44"/></svg>' },
				{ Kind: 'blurbs', Col: 9, Row: 3, ColSpan: 4, RowSpan: 8, Items: [ { Heading: 'What it does', Body: 'Describe it here.' }, { Heading: 'How it works', Body: 'Explain the flow.' } ] }
			]
		};
	}

	// ─── DOM getters + geometry ─────────────────────────────────────────────
	_el(pID) { return (typeof document !== 'undefined') ? document.getElementById(pID) : null; }
	_stage() { return this._el('BPS-Stage'); }
	_canvas() { return this._el('BPS-Canvas'); }

	_stageDims()
	{
		let tmpPair = _SIZES[this._size] || _SIZES.letter;
		let tmpAR = tmpPair[0] / tmpPair[1];
		let tmpMaxW = Math.min(840, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 560);
		let tmpMaxH = (typeof window !== 'undefined' ? window.innerHeight : 800) - 120;
		let tmpW = tmpMaxH * tmpAR, tmpH = tmpMaxH;
		if (tmpW > tmpMaxW) { tmpW = tmpMaxW; tmpH = tmpMaxW / tmpAR; }
		return [Math.round(tmpW), Math.round(tmpH)];
	}

	_cell()
	{
		let tmpBpl = this._canvas() ? this._canvas().querySelector('.bpl') : null;
		if (!tmpBpl) { return null; }
		let tmpRect = tmpBpl.getBoundingClientRect();
		let tmpGrid = this._layout.Grid;
		return { left: tmpRect.left, top: tmpRect.top, cw: tmpRect.width / tmpGrid.Cols, ch: tmpRect.height / tmpGrid.Rows };
	}

	// ─── paint ──────────────────────────────────────────────────────────────
	_sizeStage()
	{
		let tmpStage = this._stage(); if (!tmpStage) { return; }
		let tmpDims = this._stageDims();
		tmpStage.style.width = tmpDims[0] + 'px'; tmpStage.style.height = tmpDims[1] + 'px';
		tmpStage.setAttribute('data-kit', this._kit);
	}

	_drawGrid()
	{
		let tmpGridEl = this._el('BPS-Grid'); if (!tmpGridEl || !this._layout) { return; }
		let tmpGrid = this._layout.Grid; let tmpCW = 100 / tmpGrid.Cols, tmpCH = 100 / tmpGrid.Rows;
		tmpGridEl.style.backgroundImage =
			'repeating-linear-gradient(to right, var(--grid, rgba(59,111,212,.16)) 0 1px, transparent 1px ' + tmpCW + '%),'
			+ 'repeating-linear-gradient(to bottom, var(--grid, rgba(59,111,212,.16)) 0 1px, transparent 1px ' + tmpCH + '%)';
		tmpGridEl.style.backgroundSize = tmpCW + '% 100%, 100% ' + tmpCH + '%';
	}

	_paintCanvas()
	{
		let tmpCanvas = this._canvas(); if (!tmpCanvas || !this._layout) { return; }
		// Force base (landscape) placement on the canvas: the drag/resize math reads a region's base Col/Row off
		// its live rect, so a portrait alternate showing on the canvas would desync it. The editor composes the
		// base arrangement; the portrait alternate is authored as JSON/defaults and previewed on the other surfaces.
		tmpCanvas.innerHTML = libLayout.renderLayout(this._layout, { Responsive: false });
		libLayout.fitLayout(tmpCanvas);
		let tmpRegions = tmpCanvas.querySelectorAll('.bpl-region');
		for (let i = 0; i < tmpRegions.length; i++)
		{
			let tmpEl = tmpRegions[i];
			tmpEl.setAttribute('data-index', i);
			if (i === this._selected)
			{
				tmpEl.classList.add('sel');
				let tmpLabel = document.createElement('div'); tmpLabel.className = 'bps-rlabel'; tmpLabel.textContent = this._layout.Regions[i].Kind; tmpEl.appendChild(tmpLabel);
				let tmpHandle = document.createElement('div'); tmpHandle.className = 'bps-rhandle'; tmpHandle.setAttribute('data-resize', '1'); tmpEl.appendChild(tmpHandle);
			}
		}
	}

	_renderTitleBlock()
	{
		let tmpTB = this._el('BPS-TitleBlock'); if (!tmpTB) { return; }
		let tmpT = this._titleRegion() || {};
		tmpTB.innerHTML = '<div class="bps-tb-name">' + _esc(tmpT.Name || 'Untitled') + '</div>'
			+ '<div class="bps-tb-row"><div class="bps-tb-cell"><div class="bps-tb-k">Sheet</div><div class="bps-tb-v">' + _esc(tmpT.Mark || 'BP-001') + '</div></div>'
			+ '<div class="bps-tb-cell"><div class="bps-tb-k">Scale</div><div class="bps-tb-v">NTS</div></div>'
			+ '<div class="bps-tb-cell"><div class="bps-tb-k">Rev</div><div class="bps-tb-v">A</div></div></div>';
	}

	_applyActive()
	{
		['kit', 'size'].forEach((pKind) =>
		{
			let tmpWrap = this._el('BPS-' + (pKind === 'kit' ? 'Kits' : 'Sizes')); if (!tmpWrap) { return; }
			let tmpVal = (pKind === 'kit') ? this._kit : this._size;
			tmpWrap.querySelectorAll('.bps-seg').forEach((pSeg) => pSeg.classList.toggle('on', pSeg.getAttribute('data-val') === tmpVal));
		});
	}

	// ─── inspector ──────────────────────────────────────────────────────────
	_field(pIdx, pPath, pLabel, pValue, pTag)
	{
		let tmpHandler = _SELF + ".onField(" + pIdx + ",'" + pPath + "',this.value)";
		let tmpControl = (pTag === 'textarea')
			? '<textarea oninput="' + tmpHandler + '">' + _esc(pValue) + '</textarea>'
			: '<input value="' + _esc(pValue) + '" oninput="' + tmpHandler + '">';
		return '<div class="bps-field"><div class="bps-ilabel">' + _esc(pLabel) + '</div>' + tmpControl + '</div>';
	}

	_renderInspector()
	{
		let tmpIns = this._el('BPS-Inspector'); if (!tmpIns) { return; }
		let i = this._selected; let tmpR = (i >= 0 && this._layout) ? this._layout.Regions[i] : null;
		if (!tmpR) { tmpIns.innerHTML = '<div class="bps-iempty">Click a region on the sheet to edit it. Drag to move, pull the blue handle to resize. Add regions from the left, switch the drawing style and size up top.</div>'; return; }
		let h = '<div class="bps-ikind">' + _esc(tmpR.Kind) + ' region</div>';
		if (tmpR.Kind === 'title') { h += this._field(i, 'Mark', 'Mark', tmpR.Mark) + this._field(i, 'Name', 'Name', tmpR.Name) + this._field(i, 'Tagline', 'Tagline', tmpR.Tagline, 'textarea'); }
		else if (tmpR.Kind === 'note') { h += this._field(i, 'Label', 'Label', tmpR.Label) + this._field(i, 'Body', 'Body', tmpR.Body, 'textarea'); }
		else if (tmpR.Kind === 'image') { h += this._field(i, 'Alt', 'Alt', tmpR.Alt) + this._field(i, 'Caption', 'Caption', tmpR.Caption) + '<div class="bps-iempty">Drop a Media image here (coming next).</div>'; }
		else if (tmpR.Kind === 'diagram') { h += this._field(i, 'Caption', 'Caption', tmpR.Caption) + '<div class="bps-iempty">Diagram SVG comes from the module or an upload.</div>'; }
		else if (tmpR.Kind === 'blurbs')
		{
			(tmpR.Items || []).forEach((pItem, pBi) => { h += '<div class="bps-iblurb">' + this._field(i, 'Items.' + pBi + '.Heading', 'Heading', pItem.Heading) + this._field(i, 'Items.' + pBi + '.Body', 'Body', pItem.Body, 'textarea') + '</div>'; });
			h += '<button class="bps-btn" onclick="' + _SELF + '.addBlurb(' + i + ')">+ Add blurb</button>';
		}
		h += '<button class="bps-del" onclick="' + _SELF + '.deleteRegion()">Delete region</button>';
		tmpIns.innerHTML = h;
	}

	// ─── edits ──────────────────────────────────────────────────────────────
	onField(pIdx, pPath, pValue)
	{
		let tmpR = this._layout.Regions[pIdx]; if (!tmpR) { return; }
		let tmpParts = String(pPath).split('.'); let tmpObj = tmpR;
		for (let i = 0; i < tmpParts.length - 1; i++) { tmpObj = tmpObj[tmpParts[i]]; if (!tmpObj) { return; } }
		tmpObj[tmpParts[tmpParts.length - 1]] = pValue;
		this._paintCanvas(); this._renderTitleBlock(); this._markDirty();
	}

	addRegion(pKind)
	{
		let tmpDefaults =
		{
			diagram: { ColSpan: 6, RowSpan: 6, Caption: 'New diagram', SVG: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 60" fill="none" stroke="currentColor" stroke-width="2"><rect x="8" y="10" width="36" height="20"/><rect x="56" y="30" width="36" height="20"/><path d="M44 20 H56 V36"/></svg>' },
			image: { ColSpan: 6, RowSpan: 4, Alt: '', Caption: 'New image' },
			blurbs: { ColSpan: 4, RowSpan: 6, Items: [ { Heading: 'Heading', Body: 'Body text.' } ] },
			note: { ColSpan: 4, RowSpan: 2, Label: 'Label', Body: 'A short note.' },
			title: { ColSpan: 12, RowSpan: 2, Mark: 'BP', Name: 'Title', Tagline: 'A one-line description.' }
		}[pKind];
		if (!tmpDefaults) { return; }
		this._layout.Regions.push(Object.assign({ Kind: pKind, Col: 1, Row: 1 }, tmpDefaults));
		this._selected = this._layout.Regions.length - 1;
		this._markDirty(); this._paintCanvas(); this._renderInspector();
	}

	// All edits are TARGETED DOM updates, never a full template re-render: rebuilding the template would
	// recreate #BPS-Stage and drop the pointer listeners (and the inspector focus). render() is only for
	// the initial open(); the stage element itself persists across every edit here.
	addBlurb(pIdx) { let tmpR = this._layout.Regions[pIdx]; if (tmpR && tmpR.Items) { tmpR.Items.push({ Heading: 'Heading', Body: 'Body.' }); this._markDirty(); this._paintCanvas(); this._renderInspector(); } }
	deleteRegion() { if (this._selected < 0) { return; } this._layout.Regions.splice(this._selected, 1); this._selected = -1; this._markDirty(); this._paintCanvas(); this._renderInspector(); }
	selectRegion(pIdx) { this._selected = pIdx; this._paintCanvas(); this._renderInspector(); }
	// The kit lives ON the layout (layout.Kit), so it travels with {Grid,Regions} to the web page and the
	// PDF and the three outputs cannot diverge; the stage attribute is just the studio's live reflection.
	setKit(pKit) { this._kit = pKit; if (this._layout) { this._layout.Kit = pKit; } this._markDirty(); let tmpStage = this._stage(); if (tmpStage) { tmpStage.setAttribute('data-kit', pKit); } this._applyActive(); }
	setSize(pSize) { this._size = pSize; this._sizeStage(); this._drawGrid(); this._paintCanvas(); this._applyActive(); }

	// ─── drag + resize ──────────────────────────────────────────────────────
	_wire()
	{
		// Guard on the STAGE element, not the view: open() rebuilds the template (a fresh stage) when the
		// studio is opened for another blueprint, and that fresh stage needs its own listeners.
		let tmpStage = this._stage(); if (!tmpStage || tmpStage._bpsWired) { return; }
		tmpStage._bpsWired = true;
		tmpStage.addEventListener('pointerdown', (pEvent) =>
		{
			let tmpRegion = pEvent.target.closest ? pEvent.target.closest('.bpl-region') : null;
			if (!tmpRegion) { if (this._selected !== -1) { this._selected = -1; this._paintCanvas(); this._renderInspector(); } return; }
			if (pEvent.target.tagName === 'INPUT' || pEvent.target.tagName === 'TEXTAREA') { return; }
			let tmpIdx = Number(tmpRegion.getAttribute('data-index'));
			let tmpResize = !!pEvent.target.getAttribute('data-resize');
			if (this._selected !== tmpIdx) { this.selectRegion(tmpIdx); }
			let tmpReg = this._layout.Regions[tmpIdx]; let tmpCell = this._cell(); if (!tmpCell) { return; }
			let tmpEl = this._canvas().querySelector('.bpl-region[data-index="' + tmpIdx + '"]');
			this._drag = { idx: tmpIdx, resize: tmpResize, startX: pEvent.clientX, startY: pEvent.clientY, col0: tmpReg.Col, row0: tmpReg.Row, cell: tmpCell, el: tmpEl };
			try { tmpEl.setPointerCapture(pEvent.pointerId); } catch (pErr) { /* */ }
			pEvent.preventDefault();
		});
		tmpStage.addEventListener('pointermove', (pEvent) =>
		{
			if (!this._drag) { return; }
			let tmpGrid = this._layout.Grid; let tmpReg = this._layout.Regions[this._drag.idx]; let tmpCell = this._drag.cell;
			if (this._drag.resize)
			{
				let tmpCol = _clamp(Math.round((pEvent.clientX - tmpCell.left) / tmpCell.cw) + 1, this._drag.col0, tmpGrid.Cols);
				let tmpRow = _clamp(Math.round((pEvent.clientY - tmpCell.top) / tmpCell.ch) + 1, this._drag.row0, tmpGrid.Rows);
				tmpReg.ColSpan = _clamp(tmpCol - this._drag.col0 + 1, 1, tmpGrid.Cols - this._drag.col0 + 1);
				tmpReg.RowSpan = _clamp(tmpRow - this._drag.row0 + 1, 1, tmpGrid.Rows - this._drag.row0 + 1);
			}
			else
			{
				let tmpDC = Math.round((pEvent.clientX - this._drag.startX) / tmpCell.cw), tmpDR = Math.round((pEvent.clientY - this._drag.startY) / tmpCell.ch);
				tmpReg.Col = _clamp(this._drag.col0 + tmpDC, 1, tmpGrid.Cols - tmpReg.ColSpan + 1);
				tmpReg.Row = _clamp(this._drag.row0 + tmpDR, 1, tmpGrid.Rows - tmpReg.RowSpan + 1);
			}
			if (this._drag.el) { this._drag.el.style.gridColumn = tmpReg.Col + ' / span ' + tmpReg.ColSpan; this._drag.el.style.gridRow = tmpReg.Row + ' / span ' + tmpReg.RowSpan; }
		});
		let fEnd = () => { if (!this._drag) { return; } this._drag = null; this._markDirty(); this._paintCanvas(); };
		tmpStage.addEventListener('pointerup', fEnd);
		tmpStage.addEventListener('pointercancel', fEnd);
	}

	// ─── persist ────────────────────────────────────────────────────────────
	_markDirty() { try { window.localStorage.setItem(_DRAFT_KEY + this._id, JSON.stringify(this._layout)); } catch (pErr) { /* best effort */ } }

	saveLayout()
	{
		this._markDirty();
		let tmpBtn = this._el('BPS-Save');
		// Persist onto the blueprint (tenant-scoped). The localStorage draft above is the offline fallback,
		// so an in-progress design is never lost even if the save round-trip fails.
		libFrameworkClient.postJSON(this.fable, '/1.0/Blueprint/' + this._id + '/Design/Save', { Layout: this._layout })
			.then(() => { if (tmpBtn) { tmpBtn.textContent = 'Saved'; setTimeout(() => { if (tmpBtn) { tmpBtn.textContent = 'Save'; } }, 1400); } })
			.catch(() => { if (tmpBtn) { tmpBtn.textContent = 'Saved (draft)'; setTimeout(() => { if (tmpBtn) { tmpBtn.textContent = 'Save'; } }, 1400); } });
	}

	exportJSON()
	{
		let tmpJSON = JSON.stringify(this._layout, null, 2);
		if (typeof navigator !== 'undefined' && navigator.clipboard) { navigator.clipboard.writeText(tmpJSON).catch(() => {}); }
		let tmpBtn = this._el('BPS-Export'); if (tmpBtn) { tmpBtn.textContent = 'Copied'; setTimeout(() => { if (tmpBtn) { tmpBtn.textContent = 'Copy JSON'; } }, 1200); }
	}

	setPrintSheet(pSheet) { this._printSheet = pSheet; }

	// Print the ON-SCREEN layout to a one-page PDF at the chosen sheet, in the current drawing style. The
	// layout rides in the POST body (what you see is what prints); the server renders it on headless
	// Chromium. A popup carries the bytes, or they download if the popup is blocked.
	printDesign()
	{
		let tmpSheet = this._printSheet || 'Letter';
		let tmpWindow = (typeof window !== 'undefined') ? window.open('', '_blank') : null;
		let tmpBtn = this._el('BPS-Print'); if (tmpBtn) { tmpBtn.textContent = 'Printing...'; }
		let fDone = (pText) => { if (tmpBtn) { tmpBtn.textContent = pText; setTimeout(() => { if (tmpBtn) { tmpBtn.textContent = 'Print PDF'; } }, 1600); } };
		let tmpName = 'blueprint.pdf';
		fetch('/1.0/Blueprint/' + this._id + '/Design/Print', {
			method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ Sheet: tmpSheet, Kit: this._kit, Layout: this._layout })
		})
			.then((pResponse) =>
			{
				if (!pResponse.ok) { return pResponse.json().then((pBody) => Promise.reject(new Error((pBody && pBody.Error) || 'Could not print.')), () => Promise.reject(new Error('Could not print.'))); }
				let tmpDisp = pResponse.headers.get('Content-Disposition') || '';
				let tmpMatch = /filename\*=UTF-8''([^;]+)/.exec(tmpDisp) || /filename="([^"]+)"/.exec(tmpDisp);
				if (tmpMatch) { try { tmpName = decodeURIComponent(tmpMatch[1]); } catch (pErr) { tmpName = tmpMatch[1]; } }
				return pResponse.blob();
			})
			.then((pBlob) =>
			{
				let tmpURL = URL.createObjectURL(pBlob);
				if (tmpWindow) { tmpWindow.location = tmpURL; }
				else { let tmpA = document.createElement('a'); tmpA.href = tmpURL; tmpA.download = tmpName; document.body.appendChild(tmpA); tmpA.click(); document.body.removeChild(tmpA); }
				fDone('Printed');
			})
			.catch((pError) => { if (tmpWindow) { tmpWindow.close(); } fDone('Print failed'); if (typeof console !== 'undefined') { console.warn('Design print failed:', pError && pError.message); } });
	}

	back() { let tmpApp = this.pict.PictApplication; if (tmpApp && typeof tmpApp._navigate === 'function') { tmpApp._navigate('/blueprint/' + this._id); } }

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		if (this._layout)
		{
			this._sizeStage(); this._drawGrid(); this._paintCanvas(); this._renderInspector(); this._renderTitleBlock(); this._applyActive(); this._wire();
			let tmpPrintSel = this._el('BPS-PrintSheet'); if (tmpPrintSel) { tmpPrintSel.value = this._printSheet; }
			if (typeof window !== 'undefined' && !this._resizeBound) { this._resizeBound = true; window.addEventListener('resize', () => { this._sizeStage(); this._drawGrid(); this._paintCanvas(); }); }
		}
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewBlueprintStudio;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-BlueprintStudio',
	DefaultRenderable: 'Blueprint-Studio',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSSPriority: 540,
	CSS: /*css*/`
		/* A focused, full-viewport design environment: the studio takes over the screen (its own Back button
		   returns to the blueprint), so the app rail and top bar do not crowd the canvas. */
		.bps { position: fixed; inset: 0; z-index: 2000; display: grid; grid-template-columns: 192px 1fr 288px; min-height: 0; background: #101826; color: #e8edf6; font-family: "IBM Plex Sans", system-ui, sans-serif; }
		.bps-rail { background: #141d2e; overflow-y: auto; padding: 16px; }
		.bps-rail-l { border-right: 1px solid #223049; }
		.bps-rail-r { border-left: 1px solid #223049; }
		.bps-sec { font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: #6b7a95; margin: 16px 0 8px; }
		.bps-sec:first-child { margin-top: 2px; }
		.bps-btn { display: block; width: 100%; text-align: left; background: #1c293f; border: 1px solid #2b3b58; color: #dfe8f6; border-radius: 7px; padding: 9px 11px; margin-bottom: 6px; font: inherit; font-size: 13px; cursor: pointer; }
		.bps-btn:hover { background: #24344f; border-color: #3b6fd4; }
		.bps-select { width: 100%; background: #1c293f; border: 1px solid #2b3b58; color: #dfe8f6; border-radius: 7px; padding: 8px 9px; margin-bottom: 6px; font: inherit; font-size: 13px; cursor: pointer; }
		.bps-segs { display: flex; gap: 4px; background: #1c293f; border-radius: 8px; padding: 3px; }
		.bps-seg { flex: 1; text-align: center; padding: 7px 4px; border-radius: 6px; font-size: 12px; color: #aebbd2; cursor: pointer; border: none; background: transparent; font: inherit; }
		.bps-seg.on { background: #3b6fd4; color: #fff; }
		.bps-wrap { display: flex; align-items: center; justify-content: center; padding: 26px; overflow: auto; background: radial-gradient(circle at 50% 30%, #1a2537, #0d1420); }
		.bps-stage { position: relative; background: #fff; box-shadow: 0 18px 60px rgba(0,0,0,.5); padding: 2.8%;
			--bpl-ink: #12161d; --bpl-body: #2b3340; --bpl-quiet: #6b7280; --bpl-rule: #d6dbe4; --bpl-panel: #fbfcfe; --bpl-accent: #3b6fd4; --grid: rgba(59,111,212,.16); }
		.bps-grid { position: absolute; inset: 2.8%; pointer-events: none; z-index: 1; opacity: .75; }
		#BPS-Canvas { position: relative; z-index: 2; width: 100%; height: 100%; }
		.bps-stage .bpl-region { cursor: grab; }
		.bps-stage .bpl-region.sel { box-shadow: 0 0 0 2px #3b6fd4, 0 0 0 5px rgba(59,111,212,.25); }
		.bps-stage .bpl-region:hover:not(.sel) { box-shadow: 0 0 0 1.5px rgba(59,111,212,.5); }
		.bps-rhandle { position: absolute; right: -5px; bottom: -5px; width: 14px; height: 14px; background: #3b6fd4; border: 2px solid #fff; border-radius: 50%; cursor: nwse-resize; z-index: 6; box-shadow: 0 1px 4px rgba(0,0,0,.4); }
		.bps-rlabel { position: absolute; top: -9px; left: -1px; background: #3b6fd4; color: #fff; font: 600 9px "IBM Plex Mono", monospace; letter-spacing: .05em; text-transform: uppercase; padding: 1px 5px; border-radius: 3px; z-index: 6; }
		.bps-field { margin-bottom: 12px; }
		.bps-ilabel { font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: #6b7a95; margin-bottom: 4px; }
		.bps-field input, .bps-field textarea { width: 100%; background: #0f1726; border: 1px solid #2b3b58; color: #e8edf6; border-radius: 6px; padding: 7px 9px; font: inherit; font-size: 13px; box-sizing: border-box; }
		.bps-field textarea { resize: vertical; min-height: 54px; line-height: 1.4; }
		.bps-iblurb { border: 1px solid #243551; border-radius: 7px; padding: 9px; margin-bottom: 8px; background: #121b2c; }
		.bps-iempty { color: #6b7a95; font-size: 13px; line-height: 1.5; }
		.bps-ikind { font-family: "IBM Plex Mono", monospace; font-size: 11px; letter-spacing: .1em; text-transform: uppercase; color: #8fb4ff; margin-bottom: 14px; }
		.bps-del { background: #3a1622; border: 1px solid #6b2740; color: #ffb4c4; border-radius: 7px; padding: 8px 11px; font: inherit; font-size: 13px; cursor: pointer; width: 100%; margin-top: 8px; }
		.bps-logo { font-family: "IBM Plex Mono", monospace; font-size: 12px; letter-spacing: .14em; color: #8fb4ff; text-transform: uppercase; margin-bottom: 4px; }
		/* drawing-style chrome: a drafting frame + corner title block, shown for drafting/blueprint kits */
		.bps-frame { display: none; position: absolute; inset: 1.5%; border: 1.5px solid var(--bpl-ink, #222); pointer-events: none; z-index: 3; }
		.bps-frame::after { content: ''; position: absolute; inset: 4px; border: 0.75px solid var(--bpl-rule, #999); }
		.bps-tb { display: none; position: absolute; right: 3%; bottom: 3%; width: 31%; z-index: 4; border: 1.25px solid var(--bpl-ink, #222); background: var(--tb-bg, #f5f1e8); font-family: "IBM Plex Mono", monospace; color: var(--bpl-ink, #222); }
		.bps-tb-name { padding: 6px 9px; border-bottom: 1px solid var(--bpl-ink, #222); font-weight: 700; font-size: 12px; letter-spacing: .05em; text-transform: uppercase; }
		.bps-tb-row { display: flex; }
		.bps-tb-cell { flex: 1; padding: 5px 9px; border-right: 0.75px solid var(--bpl-rule, #999); }
		.bps-tb-cell:last-child { border-right: 0; }
		.bps-tb-k { font-size: 7.5px; letter-spacing: .09em; color: var(--bpl-quiet, #777); text-transform: uppercase; margin-bottom: 1px; }
		.bps-tb-v { font-size: 11px; }
		.bps-stage[data-kit="drafting"] .bps-frame, .bps-stage[data-kit="blueprint"] .bps-frame,
		.bps-stage[data-kit="drafting"] .bps-tb, .bps-stage[data-kit="blueprint"] .bps-tb { display: block; }
		.bps-stage[data-kit="drafting"] { background: #f5f1e8; --tb-bg: #f5f1e8; --bpl-ink: #232730; --bpl-body: #2d343d; --bpl-quiet: #6b7480; --bpl-rule: #b2ab98; --bpl-panel: transparent; --bpl-accent: #232730; --grid: rgba(70,62,45,.11); }
		.bps-stage[data-kit="blueprint"] { background: #15325f; --tb-bg: #15325f; --bpl-ink: #eaf3ff; --bpl-body: #d2e2f6; --bpl-quiet: #9ec0ec; --bpl-rule: rgba(255,255,255,.42); --bpl-panel: rgba(255,255,255,.05); --bpl-accent: #8fd3ff; --grid: rgba(175,208,255,.17); }
		.bps-stage[data-kit="drafting"] .bpl-region, .bps-stage[data-kit="blueprint"] .bpl-region,
		.bps-stage[data-kit="drafting"] .bpl-diagram, .bps-stage[data-kit="blueprint"] .bpl-diagram,
		.bps-stage[data-kit="drafting"] .bpl-image img, .bps-stage[data-kit="blueprint"] .bpl-image img,
		.bps-stage[data-kit="drafting"] .bpl-image-empty, .bps-stage[data-kit="blueprint"] .bpl-image-empty { border-radius: 0 !important; }
		.bps-stage[data-kit="drafting"] .bpl-diagram, .bps-stage[data-kit="blueprint"] .bpl-diagram { background: transparent; border-width: 0.12cqh; border-color: var(--bpl-rule); }
		.bps-stage[data-kit="drafting"] .bpl-title-name, .bps-stage[data-kit="blueprint"] .bpl-title-name { text-transform: uppercase; letter-spacing: .015em; }
		@media (max-width: 900px) { .bps { grid-template-columns: 1fr; } .bps-rail { display: none; } }
	` + libLayout.LAYOUT_CSS,
	Templates:
	[
		{
			Hash: 'Blueprint-Studio',
			Template: /*html*/`
<div class="bps">
	<aside class="bps-rail bps-rail-l">
		<div class="bps-logo">Blueprint Studio</div>
		<div class="bps-sec">Add region</div>
		<button class="bps-btn" onclick="${_SELF}.addRegion('diagram')">+ Diagram</button>
		<button class="bps-btn" onclick="${_SELF}.addRegion('image')">+ Image</button>
		<button class="bps-btn" onclick="${_SELF}.addRegion('blurbs')">+ Blurbs</button>
		<button class="bps-btn" onclick="${_SELF}.addRegion('note')">+ Note</button>
		<button class="bps-btn" onclick="${_SELF}.addRegion('title')">+ Title block</button>
		<div class="bps-sec">Drawing style</div>
		<div class="bps-segs" id="BPS-Kits">
			<button class="bps-seg" data-val="clean" onclick="${_SELF}.setKit('clean')">Clean</button>
			<button class="bps-seg" data-val="drafting" onclick="${_SELF}.setKit('drafting')">Drafting</button>
			<button class="bps-seg" data-val="blueprint" onclick="${_SELF}.setKit('blueprint')">Blueprint</button>
		</div>
		<div class="bps-sec">Preview size</div>
		<div class="bps-segs" id="BPS-Sizes">
			<button class="bps-seg" data-val="web" onclick="${_SELF}.setSize('web')">Web</button>
			<button class="bps-seg" data-val="letter" onclick="${_SELF}.setSize('letter')">Letter</button>
			<button class="bps-seg" data-val="archd" onclick="${_SELF}.setSize('archd')">ArchD</button>
		</div>
		<div class="bps-sec">Print</div>
		<select class="bps-select" id="BPS-PrintSheet" onchange="${_SELF}.setPrintSheet(this.value)">
			<option value="Letter">Letter (8.5 x 11 in)</option>
			<option value="Legal">Legal (8.5 x 14 in)</option>
			<option value="Tabloid">Tabloid (11 x 17 in)</option>
			<option value="ArchD">ARCH D (24 x 36 in)</option>
			<option value="ArchDLandscape">ARCH D landscape</option>
		</select>
		<button class="bps-btn" id="BPS-Print" onclick="${_SELF}.printDesign()" title="Print this layout as a one-page PDF at the chosen size, in the current drawing style">Print PDF</button>
		<div class="bps-sec">Layout</div>
		<button class="bps-btn" id="BPS-Save" onclick="${_SELF}.saveLayout()">Save</button>
		<button class="bps-btn" id="BPS-Export" onclick="${_SELF}.exportJSON()">Copy JSON</button>
		<button class="bps-btn" onclick="${_SELF}.back()">Back to blueprint</button>
	</aside>
	<main class="bps-wrap">
		<div class="bps-stage" id="BPS-Stage" data-kit="drafting">
			<div class="bps-grid" id="BPS-Grid"></div>
			<div class="bps-frame"></div>
			<div id="BPS-Canvas"></div>
			<div class="bps-tb" id="BPS-TitleBlock"></div>
		</div>
	</main>
	<aside class="bps-rail bps-rail-r">
		<div class="bps-sec">Inspector</div>
		<div id="BPS-Inspector"></div>
	</aside>
</div>`
		}
	],
	Renderables:
	[
		{ RenderableHash: 'Blueprint-Studio', TemplateHash: 'Blueprint-Studio', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};
